import { Panel, Chip } from "@/components/lux";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MoneyField, NumField, FieldRow, MiniToggle, Divider } from "./fields";
import { Landmark } from "lucide-react";
import { FREQUENCIES, FREQ_LABEL } from "@/dealership/lib/desk";

const TERMS = [12, 24, 36, 48, 60, 72, 84, 96];

export default function FinancePanel({ ws, set, settings }) {
  const fin = ws.finance || {};
  const setFin = (patch) => set({ finance: { ...fin, ...patch } });
  const lenders = settings?.lenders || [];
  const lender = lenders.find((l) => l.id === fin.lender_id);

  const pickLender = (id) => {
    const l = lenders.find((x) => x.id === id);
    if (!l) return setFin({ lender_id: "", lender_name: "" });
    // Suggest the best matching tier for this vehicle year + term (always overridable).
    const year = Number(ws.vehicle_year) || 0;
    const term = Number(fin.term_months) || 0;
    const tier = (l.tiers || []).find((t) => year >= (t.min_year || 0) && term <= (t.max_term || 999)) || (l.tiers || [])[0];
    setFin({ lender_id: id, lender_name: l.name, ...(tier ? { apr: Number(tier.rate) } : {}) });
  };

  return (
    <Panel label="Financing" title={fin.mode === "cash" ? "Cash purchase" : "Structure"} icon={Landmark} testid="desk-finance-panel">
      <div className="space-y-4">
        <div className="flex gap-2">
          <Chip active={fin.mode !== "cash"} onClick={() => setFin({ mode: "finance" })} testid="desk-mode-finance">Finance</Chip>
          <Chip active={fin.mode === "cash"} onClick={() => setFin({ mode: "cash" })} testid="desk-mode-cash">Cash</Chip>
        </div>

        <FieldRow label="Cash down">
          <MoneyField value={fin.cash_down} onChange={(v) => setFin({ cash_down: v })} testid="desk-cash-down" />
        </FieldRow>
        <FieldRow label="Deposit received">
          <MoneyField value={fin.deposit} onChange={(v) => setFin({ deposit: v })} testid="desk-deposit" />
        </FieldRow>

        {fin.mode !== "cash" && (
          <>
            <Divider />
            {lenders.length > 0 && (
              <div>
                <div className="label-mono mb-2">Lender</div>
                <Select value={fin.lender_id || ""} onValueChange={pickLender}>
                  <SelectTrigger className="h-9" data-testid="desk-lender-select"><SelectValue placeholder="No lender selected" /></SelectTrigger>
                  <SelectContent>
                    {lenders.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {lender && (
                  <div className="flex gap-1.5 flex-wrap mt-2">
                    {(lender.tiers || []).map((t, i) => (
                      <button key={i} onClick={() => setFin({ apr: Number(t.rate) })} data-testid={`desk-tier-${i}`}
                        className="px-2.5 py-1 rounded-full border border-border text-[11px] text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors">
                        {t.label} · {t.rate}%
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <FieldRow label="APR">
              <NumField value={fin.apr} onChange={(v) => setFin({ apr: v })} step={0.01} suffix="%" testid="desk-apr" />
            </FieldRow>

            <div>
              <div className="label-mono mb-2">Term (months)</div>
              <div className="flex gap-1.5 flex-wrap">
                {TERMS.map((t) => (
                  <button key={t} onClick={() => setFin({ term_months: t })} data-testid={`desk-term-${t}`}
                    className={`px-3 py-1.5 rounded-full border text-xs tnum transition-colors ${
                      Number(fin.term_months) === t ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
                    }`}>{t}</button>
                ))}
                <div className="w-24">
                  <div className="text-[9px] uppercase tracking-widest text-muted-foreground/70 mb-0.5">Custom</div>
                  <NumField value={fin.term_months} onChange={(v) => setFin({ term_months: v })} testid="desk-term-custom" />
                </div>
              </div>
            </div>

            <div>
              <div className="label-mono mb-2">Payment frequency</div>
              <div className="flex gap-1.5 flex-wrap">
                {FREQUENCIES.map((f) => (
                  <button key={f} onClick={() => setFin({ frequency: f })} data-testid={`desk-freq-${f}`}
                    className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${
                      (fin.frequency || "biweekly") === f ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
                    }`}>{FREQ_LABEL[f]}</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="label-mono mb-2">First payment date</div>
                <Input type="date" value={fin.first_payment_date || ""} onChange={(e) => setFin({ first_payment_date: e.target.value })} className="h-9" data-testid="desk-first-payment" />
              </div>
              <div>
                <div className="label-mono mb-2">Deferral (days)</div>
                <NumField value={fin.deferral_days} onChange={(v) => setFin({ deferral_days: v })} testid="desk-deferral" />
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 pt-1">
              <div className="label-mono">Round payment up to the dollar</div>
              <MiniToggle on={!!fin.round_payment} onClick={() => setFin({ round_payment: !fin.round_payment })} onLabel="Rounded" offLabel="Exact" testid="desk-round-payment" />
            </div>
          </>
        )}
      </div>
    </Panel>
  );
}
